<template>
  <DeepDialog
    v-model="model"
    data-test-id="deepa-chat-export-dialog"
    :max-width="560"
    :title="t('deepa_chat.export.title')"
  >
    <template #content>
      <v-form
        ref="formRef"
        class="d-flex flex-column ga-4"
        @submit.prevent="onSave"
      >
        <v-select
          v-model="format"
          hide-details
          :items="formatItems"
          :label="t('deepa_chat.export.format')"
          name="field-deepa-chat-export-format"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <DeepNodeIcon
                  class="me-3"
                  height="20"
                  :mime-type="item.mimeType"
                  width="20"
                />
              </template>
            </v-list-item>
          </template>
          <template #selection="{ item }">
            <div class="d-flex align-center ga-3">
              <DeepNodeIcon height="20" :mime-type="item.mimeType" width="20" />
              {{ item.title }}
            </div>
          </template>
        </v-select>
        <v-text-field
          v-model="fileName"
          autocomplete="off"
          hide-details="auto"
          :label="t('deepa_chat.export.file_name')"
          name="field-deepa-chat-export-file-name"
          :rules="[ruleRequired]"
          :suffix="`.${formatDefinition.extension}`"
        />
        <div>
          <div class="text-body-medium text-medium-emphasis mb-1">
            {{ t('deepa_chat.export.location') }}
          </div>
          <v-sheet border rounded>
            <DKitDeepBoxNodeNavigation
              v-if="bearer"
              :key="openCount"
              :api-token="bearer"
              :box-id
              :disabled="isSaving"
              :env
              :start-node-id
              :type-id
              @change:select="folder = $event"
            />
          </v-sheet>
          <div
            v-if="folder && !canSaveInFolder"
            class="text-body-small text-error mt-1"
          >
            {{ t('deepa_chat.export.no_write_permission') }}
          </div>
        </div>
      </v-form>
    </template>
    <template #actions>
      <v-spacer />
      <VBtnSecondary :disabled="isSaving" @click="model = false">
        {{ t('dialogs.cancel') }}
      </VBtnSecondary>
      <VBtnPrimary :disabled="!canSave" :loading="isSaving" @click="onSave">
        {{ t('buttons.save') }}
      </VBtnPrimary>
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import { toast } from 'vue-sonner'
import { useContextDeepAChat } from '@/pocs/chat/composables/ctx-deepa-chat'
import { useDeepAChatExport } from '@/pocs/chat/export/chat-export'
import { useDeepAChatTranscript } from '@/pocs/chat/export/chat-transcript'
import {
  getDefaultExportFileName,
  sanitizeFileName,
} from '@/pocs/chat/export/file-name'
import {
  CHAT_EXPORT_FORMATS,
  DEFAULT_CHAT_EXPORT_FORMAT,
  getChatExportFormat,
} from '@/pocs/chat/export/formats'
import { DKitDeepBoxNodeNavigation } from '@deepcloud/deep-ui-kit'
import { DeepDialog, DeepNodeIcon } from '@deepcloud/deep-ui-lib'
import {
  isNodeMimeTypeFiles,
  isNodeMimeTypeInbox,
  isNodeMimeTypeTrash,
} from '@deepcloud/deep-ui-utils'
import { ruleRequired } from '@deepcloud/field-validators'
import { useKeycloak } from '@/composables/auth/keycloak'
import { useNodePolicy } from '@/composables/node/node-policy'
import type { ChatExportFormat, ChatTranscript } from '@/pocs/chat/export/types'
import type { DeepAChatContextNode } from '@/pocs/chat/types'
import type { PathSegment } from '@deepcloud/api-deep-box'

const model = defineModel({ type: Boolean, default: false })

const { t } = useI18n()
const route = useRoute()
const { bearer } = useKeycloak()
const env = import.meta.env.VITE_ENVIRONMENT
const { canAddChildren } = useNodePolicy()

const { messages, contextNodes } = useContextDeepAChat()
const buildTranscript = useDeepAChatTranscript()
const { exportChat } = useDeepAChatExport()

const typeId = computed(() => String(route.params.type ?? ''))
const boxId = computed(() => String(route.params.box ?? ''))

const formRef = useTemplateRef('formRef')
const format = ref<ChatExportFormat>(DEFAULT_CHAT_EXPORT_FORMAT)
const fileName = ref('')
const folder = ref<PathSegment>()
const transcript = shallowRef<ChatTranscript>()
const isSaving = ref(false)
const openCount = ref(0)

const formatItems = computed(() =>
  CHAT_EXPORT_FORMATS.map(({ id, mimeType }) => ({
    title: t(`deepa_chat.export.formats.${id}`),
    value: id,
    mimeType,
  })),
)

const formatDefinition = computed(() => getChatExportFormat(format.value))

function isSectionRoot({ mimeType }: DeepAChatContextNode) {
  return (
    !!mimeType &&
    (isNodeMimeTypeFiles(mimeType) ||
      isNodeMimeTypeInbox(mimeType) ||
      isNodeMimeTypeTrash(mimeType))
  )
}

const startNodeId = computed(() => {
  const [firstFolder] = contextNodes.value
  return firstFolder && !isSectionRoot(firstFolder) ? firstFolder.nodeId : ''
})

const canSaveInFolder = computed(
  () => !!folder.value && canAddChildren(folder.value),
)

const canSave = computed(
  () =>
    canSaveInFolder.value &&
    !!sanitizeFileName(fileName.value) &&
    !isSaving.value,
)

watch(model, (isOpen) => {
  if (!isOpen) return
  openCount.value++
  transcript.value = buildTranscript(messages.value, contextNodes.value)
  fileName.value = getDefaultExportFileName(
    t('deepa_chat.export.file_name_prefix'),
    transcript.value,
  )
  format.value = DEFAULT_CHAT_EXPORT_FORMAT
  folder.value = undefined
})

async function onSave() {
  const result = await formRef.value?.validate()
  if (!result?.valid || !canSave.value || !transcript.value || !folder.value) {
    return
  }

  isSaving.value = true
  try {
    const { node, leftoverFileName } = await exportChat({
      transcript: transcript.value,
      format: format.value,
      fileName: sanitizeFileName(fileName.value),
      typeId: typeId.value,
      boxId: boxId.value,
      folder: folder.value,
    })
    toast.success(t('deepa_chat.export.success', { name: node.displayName }))
    if (leftoverFileName) {
      toast.warning(
        t('deepa_chat.export.leftover_file', { name: leftoverFileName }),
      )
    }
    model.value = false
  } catch {
    toast.error(t('error.error_occurred'))
  } finally {
    isSaving.value = false
  }
}
</script>
